import React from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import heroImage from "../../assets/images/homepage/preview.png";
import schedulingCalendar from "../../assets/images/homepage/scheduling-calendar.png";
import paymentMethodsImage from "../../assets/images/homepage/payment-methods.png";
import customizationPreview from "../../assets/images/homepage/different-styles.png";
import Footer from "../Footer";
import {
  ArrowRightIcon,
  BellIcon,
  CalendarCheckIcon,
  CardIcon,
  CheckIcon,
  ClockIcon,
  DollarIcon,
  PaletteIcon,
  PencilIcon,
  ScissorsIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "../public/icons";
import {
  formatSubscriptionInterval,
  formatSubscriptionPrice,
  getPublishedSubscriptionPlan,
} from "../../constants/subscriptionPlans";

const singleModePlan = getPublishedSubscriptionPlan("single");

const heroPerks = [
  "Take payments upfront",
  "Protect your time with buffers",
  "Automate reminders and follow ups",
  "Customize your booking page",
];

const workflowSteps = [
  {
    number: "01",
    title: "Set up your services",
    description:
      "Import your services or start from our ready-made templates, then set the hours and buffers you actually want to work.",
    tone: "clay",
  },
  {
    number: "02",
    title: "Share your booking link",
    description:
      "Add it to your bio, your website, or send it directly to clients. They book instantly against your real availability.",
    tone: "sage",
  },
  {
    number: "03",
    title: "Get booked and paid",
    description:
      "Reminders, deposits, and follow-ups fire off automatically. You get paid upfront, before the appointment starts.",
    tone: "clay",
  },
];

const essentials = [
  {
    title: "Smart scheduling",
    description:
      "Clients book instantly based on your availability. No double bookings. No back-and-forth.",
    icon: CalendarCheckIcon,
    tone: "clay",
  },
  {
    title: "Client management",
    description: "Notes, preferences, and visit history — all in one place.",
    icon: UsersIcon,
    tone: "sage",
  },
  {
    title: "Automated reminders",
    description: "Email reminders and follow-ups that reduce no-shows.",
    icon: BellIcon,
    tone: "clay",
  },
  {
    title: "Custom booking pages",
    description: "Start from one clean, ready-made template and make it yours.",
    icon: PaletteIcon,
    tone: "sage",
  },
];

const paymentBenefits = [
  {
    title: "Card payments (Stripe)",
    description: "Visa, Mastercard, Amex, and more",
    icon: CardIcon,
  },
  {
    title: "Cash App",
    description: "Fast, direct payments",
    icon: DollarIcon,
  },
  {
    title: "Klarna BNPL (you get paid upfront)",
    description: "Clients can split payments, you get paid immediately",
    icon: ClockIcon,
  },
  {
    title: "Secure checkout via Stripe",
    description: "Bank-level encryption and fraud protection",
    icon: ShieldCheckIcon,
  },
];

const customizationPoints = [
  "Swap colors, fonts, page width, and background shapes to match your brand.",
  "Create custom booking flows that suit your services.",
  "Connect your domain so your booking page feels like home.",
];

const Home = () => {
  const singlePrice = formatSubscriptionPrice(singleModePlan.price, "en-US");
  const singleInterval = formatSubscriptionInterval(singleModePlan.price);

  return (
    <>
      <main className="home-shell">
        {/* Hero */}
        <section className="home-hero">
          <span className="deco deco--1 home-hero__deco-a" aria-hidden="true" />
          <span className="deco deco--2 home-hero__deco-b" aria-hidden="true" />
          <div className="home-hero__grid">
            <div className="home-hero__copy">
              <span className="tag tag-sage">
                <ScissorsIcon size={14} />
                Built for independent professionals
              </span>
              <h1>Run your business without the chaos of scheduling.</h1>
              <p className="lead">
                Haastia is an all-in-one scheduling and client management platform designed for
                independent professionals. Accept payments, manage clients, automate reminders, and
                get paid upfront — all from one calm dashboard.
              </p>
              <div className="home-hero__actions">
                <Link className="btn btn-primary btn-lg" to="/signup">
                  Start free trial
                </Link>
                <Link className="btn btn-secondary btn-lg" to="/example">
                  See how it works
                  <ArrowRightIcon size={16} />
                </Link>
              </div>
              <p className="home-hero__price">
                <span className="home-hero__price-value">{singlePrice}</span>
                <span className="home-hero__price-cadence">/{singleInterval}</span>
                <span>No contracts · Cancel anytime</span>
              </p>
            </div>

            <div className="home-hero__media">
              <span className="deco deco--3 home-hero__media-deco" aria-hidden="true" />
              <img
                className="washed home-hero__image"
                src={heroImage}
                alt="A Haastia booking page on a phone, showing service times a client can pick"
              />
            </div>
          </div>
        </section>
        <div className="dashed-rule" aria-hidden="true" />

        {/* How it works */}
        <section className="home-steps" id="how-it-works">
          <div className="home-steps__intro">
            <p className="eyebrow">How it works</p>
            <h2>Share your link. Get booked. Get paid.</h2>
            <p className="lead">Set up once, share one link, and let everything else run automatically.</p>
          </div>
          <ol className="home-steps__grid">
            {workflowSteps.map((step) => (
              <li className={`home-step home-step--${step.tone}`} key={step.number}>
                <span className="home-step__number" aria-hidden="true">
                  {step.number}
                </span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </section>
        <div className="home-rule--pencil" aria-hidden="true">
          <div className="dashed-rule" />
          <span className="home-rule__pencil">
            <PencilIcon size={42} />
          </span>
        </div>

        {/* Stress-free scheduling */}
        <section className="home-band">
          <div className="home-split">
            <div className="home-split__media">
              <span className="deco deco--2 home-split__media-deco" aria-hidden="true" />
              <img
                className="washed home-split__graphic"
                src={schedulingCalendar}
                alt="A month calendar of confirmed bookings next to a list of the day's appointments"
                loading="lazy"
              />
            </div>
            <div>
              <p className="eyebrow">Stress-free scheduling</p>
              <h2>Scheduling shouldn't feel like a second job.</h2>
              <p className="lead">
                Send one link and let Haastia handle confirmations, reminders, and rebooking nudges.
                Keep your calendar organized while you focus on your craft.
              </p>
              <ul className="check-list">
                {heroPerks.map((perk) => (
                  <li key={perk}>
                    <CheckIcon size={19} />
                    {perk}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Why Haastia */}
        <section className="home-why">
          <div className="home-why__intro">
            <p className="eyebrow">Why Haastia</p>
            <h2>Set up once. Let the rest run.</h2>
            <p>Four things that quietly remove the busywork between clients.</p>
          </div>
          <div className="home-why__features">
            {essentials.map(({ title, description, icon: Icon, tone }) => (
              <article className="home-feature" key={title}>
                <span className={`icon-circle${tone === "sage" ? " icon-circle--sage" : ""}`}>
                  <Icon size={24} />
                </span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Get paid upfront */}
        <section className="home-payments">
          <div className="home-payments__panel">
            <span className="deco home-payments__deco" aria-hidden="true" />
            <div className="home-payments__grid">
              <div>
                <p className="eyebrow eyebrow--light">Get paid upfront</p>
                <h2>Zero risk. More calm.</h2>
                <p className="lead">
                  Offer cards, Cash App, or Klarna. Require deposits, prevent no-shows, and track
                  every transaction in one place.
                </p>
                <ul className="home-payments__list">
                  {paymentBenefits.map(({ title, description, icon: Icon }) => (
                    <li key={title}>
                      <span className="icon-circle icon-circle--light">
                        <Icon size={19} />
                      </span>
                      <span>
                        <strong>{title}</strong>
                        <span>{description}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="home-payments__media">
                <img
                  className="washed home-payments__image"
                  src={paymentMethodsImage}
                  alt="Checkout options a client sees when paying for a booking"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Customization */}
        <section className="home-custom">
          <div className="home-custom__grid">
            <div>
              <p className="eyebrow">Customization</p>
              <h2>Your brand, your rules.</h2>
              <p className="lead">
                Haastia helps you customize booking pages, confirmations, and reminders so every
                client touchpoint feels like you.
              </p>
              <ul className="home-custom__list">
                {customizationPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <div className="home-custom__actions">
                <Link className="btn btn-secondary btn-md" to="/signup">
                  Start Free Trial
                </Link>
                <span className="hand">change any of it, any time</span>
              </div>
            </div>
            <div className="home-custom__media">
              <img
                className="washed home-custom__image"
                src={customizationPreview}
                alt="Three booking pages styled differently by their owners"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Final call to action */}
        <section className="home-cta">
          <div className="home-cta__panel">
            <span className="deco deco--2 home-cta__deco" aria-hidden="true" />
            <div className="home-cta__grid">
              <div>
                <h2>Bring calm to your calendar.</h2>
                <p>Launch in an afternoon. Let clients book anytime. Get paid without stress.</p>
              </div>
              <div className="home-cta__actions">
                <Link className="btn btn-primary btn-lg" to="/signup">
                  Create your account
                  <ArrowRightIcon size={17} />
                </Link>
                <Link className="btn btn-secondary btn-lg" to="/pricing">
                  See what's included
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Home;
